import { type PlanningQueueDto, type TaskDto } from '@bqcrm/shared';
import { useTaskTypeLabel } from '../../data';
import { Badge, Card, Group, Modal, ScrollArea, Stack, Text, TextInput } from '@mantine/core';
import { IconSearch } from '@tabler/icons-react';
import { useState } from 'react';
import { TradeBadge } from '../../components/Badges';
import { EmptyState } from '../../components/ui';
import { FleetWarnings, longDay } from './common';

type Scope = PlanningQueueDto['scopes'][number];

/**
 * Przypisanie zadania do ekipy (od strony ekipy): kliknięty dzień w wierszu ekipy → wybór zadania.
 * Ta sama ekipa i dzień trafiają do okna planu; zadanie może mieć kilka ekip jednocześnie.
 */
export function AssignDialog(p: {
  teamName: string;
  day: string;
  queue: PlanningQueueDto | undefined;
  /** zadania zaplanowane / w toku w widocznym okresie — można dołożyć kolejną ekipę */
  active: TaskDto[];
  /** Flota (docs/19 §6): auto tej ekipy niedostępne w tym dniu */
  fleetWarnings?: string[];
  onTask: (t: TaskDto) => void;
  onScope: (s: Scope) => void;
  onClose: () => void;
}) {
  const typeLabel = useTaskTypeLabel();
  const [q, setQ] = useState('');
  const m = (...xs: (string | null | undefined)[]) => !q.trim() || xs.some((x) => x?.toLowerCase().includes(q.trim().toLowerCase()));
  const tm = (t: TaskDto) => m(t.number, t.title, t.clientName, t.siteAddress, t.caseNumber, t.ticketNumber);
  const groups: { label: string; tasks?: TaskDto[]; scopes?: Scope[] }[] = [
    { label: 'W toku — kolejne dni', tasks: (p.queue?.continue ?? []).filter(tm) },
    { label: 'Do zaplanowania', tasks: (p.queue?.tasks ?? []).filter(tm) },
    { label: 'Realizacje (nowe zadanie)', scopes: (p.queue?.scopes ?? []).filter((s) => m(s.clientName, s.city, s.caseNumber, s.tradeCode)) },
    { label: 'Zaplanowane — dołóż tę ekipę', tasks: p.active.filter(tm) },
  ];
  const row = (key: string, onClick: () => void, top: React.ReactNode, title: string, sub: string) => (
    <Card key={key} withBorder padding={8} className="card-link" onClick={onClick}>
      <Group justify="space-between" wrap="nowrap">
        <div style={{ minWidth: 0 }}>
          <Group gap={4}>{top}</Group>
          <Text size="sm" fw={600} truncate>
            {title}
          </Text>
          <Text size="xs" c="dimmed" truncate>
            {sub}
          </Text>
        </div>
        <Text size="xs" c="var(--app-link)" fw={600} style={{ flexShrink: 0 }}>
          Przypisz ›
        </Text>
      </Group>
    </Card>
  );
  return (
    <Modal opened onClose={p.onClose} title={`Przypisanie zadania: ${p.teamName}, ${longDay(p.day)}`} size="lg">
      <Stack gap="sm">
        <FleetWarnings warnings={p.fleetWarnings} />
        <TextInput leftSection={<IconSearch size={14} />} placeholder="Klient, miejscowość, numer" value={q} onChange={(e) => setQ(e.currentTarget.value)} data-autofocus />
        <ScrollArea.Autosize mah="60vh">
          <Stack gap="md">
            {groups
              .filter((g) => (g.tasks ?? g.scopes ?? []).length > 0)
              .map((g) => (
                <Stack key={g.label} gap={6}>
                  <Text size="xs" fw={700} c="dimmed" tt="uppercase">
                    {g.label}
                  </Text>
                  {g.tasks?.map((t) =>
                    row(
                      t.id,
                      () => p.onTask(t),
                      <>
                        <Badge size="xs" variant="light" color="gray">
                          {typeLabel(t)}
                        </Badge>
                        {t.scopes.slice(0, 2).map((s) => (
                          <TradeBadge key={s.id} code={s.tradeCode} />
                        ))}
                      </>,
                      t.clientName ?? t.title,
                      `${t.siteAddress ?? ''} · ${t.ticketNumber ?? t.caseNumber ?? t.number}`,
                    ),
                  )}
                  {g.scopes?.map((s) =>
                    row(
                      s.contractId,
                      () => p.onScope(s),
                      <>
                        <Badge size="xs" variant="light" color="gray">
                          Realizacja
                        </Badge>
                        {s.tradeCodes.map((c) => (
                          <TradeBadge key={c} code={c} />
                        ))}
                      </>,
                      s.clientName,
                      `${s.city} · ${s.contractNumber ?? s.caseNumber}${s.conditionsMet ? '' : ' · warunkowo (warunki startu niespełnione)'}`,
                    ),
                  )}
                </Stack>
              ))}
            {groups.every((g) => !(g.tasks ?? g.scopes ?? []).length) && <EmptyState compact title={q.trim() ? 'Brak wyników' : 'Brak zadań do przypisania'} />}
          </Stack>
        </ScrollArea.Autosize>
      </Stack>
    </Modal>
  );
}
